// ============================================================================
// Internal linking plan for sector landing pages.
// Each industry maps to the most relevant HR help articles, service pages,
// calculators and templates so crawlers (and LLMs) get short, topical paths
// between the sector hub and the supporting content that proves depth.
// ============================================================================
import { getBlogPost } from "@/content/blog";
import { getService } from "@/content/services";
import { getTemplate } from "@/content/templates";
import { getTool } from "@/content/tools";

export interface PlannedLink {
  /** Absolute site path. */
  path: string;
  /** Descriptive anchor text, never generic ("learn more"). */
  label: string;
  /** One-line reason the link is relevant to this sector. */
  context: string;
  /** Underlying content slug, used for analytics payloads. */
  slug?: string;
  /** Short qualifier shown as a badge (tool category, template format). */
  meta?: string;
}

export interface IndustryLinkPlan {
  articles: PlannedLink[];
  services: PlannedLink[];
  calculators: PlannedLink[];
  templates: PlannedLink[];
}

interface PlanSpec {
  articles: Array<[slug: string, context: string]>;
  services: Array<[slug: string, context: string]>;
  calculators: Array<[slug: string, context: string]>;
  templates: Array<[slug: string, context: string]>;
}

const DEFAULT_SPEC: PlanSpec = {
  articles: [
    ["disciplinary-hearing-checklist", "Run conduct hearings that stand up to scrutiny."],
    ["uk-redundancy-process-step-by-step", "Follow a defensible redundancy process end to end."],
    ["handling-flexible-working-requests", "Handle statutory requests within the legal timescales."],
  ],
  services: [],
  calculators: [
    ["holiday-entitlement-calculator", "Check part-year and irregular-hours entitlement."],
    ["notice-period-calculator", "Confirm statutory and contractual notice before you write."],
    ["ssp-calculator", "Work out sick pay for absence that runs on."],
  ],
  templates: [
    ["employment-contract-uk", "Issue a compliant written statement on day one."],
    ["disciplinary-invitation-letter", "Invite to a hearing with the right wording."],
  ],
};

const SPECS: Record<string, Partial<PlanSpec>> = {
  hospitality: {
    articles: [
      ["handling-flexible-working-requests", "Shift patterns and split shifts attract flexible working requests."],
      ["disciplinary-hearing-checklist", "Conduct issues on shift need a clean paper trail."],
      ["workplace-harassment-employer-guide", "The preventative duty bites hardest in customer-facing venues."],
    ],
    calculators: [
      ["national-minimum-wage-calculator", "Check tips, deductions and uniform costs against NMW."],
      ["holiday-entitlement-calculator", "Calculate holiday for irregular-hours and seasonal staff."],
      ["ssp-calculator", "Cover sick pay across zero-hours and casual rotas."],
    ],
    templates: [
      ["right-to-work-check-record", "Keep right to work checks audit-ready across venues."],
      ["employment-contract-uk", "Set out shift, tronc and overtime terms in writing."],
    ],
  },
  healthcare: {
    articles: [
      ["disciplinary-hearing-checklist", "Clinical conduct cases run alongside regulator referrals."],
      ["workplace-harassment-employer-guide", "Patient-facing teams need active harassment prevention."],
      ["acas-early-conciliation-explained", "Know what happens once a clinician contacts ACAS."],
    ],
    calculators: [
      ["ssp-calculator", "Model sick pay for long-term clinical absence."],
      ["maternity-pay-calculator", "Plan SMP across a heavily rota'd workforce."],
      ["notice-period-calculator", "Check notice before ending a registrant's employment."],
    ],
    templates: [
      ["right-to-work-check-record", "Track visa expiry for internationally recruited staff."],
      ["disciplinary-invitation-letter", "Invite to a fitness-to-practise linked hearing."],
    ],
  },
  "care-homes": {
    articles: [
      ["disciplinary-hearing-checklist", "Safeguarding-linked conduct cases need exact process."],
      ["workplace-harassment-employer-guide", "Meet the preventative duty across night and day teams."],
      ["uk-redundancy-process-step-by-step", "Restructure safely when occupancy or funding drops."],
    ],
    calculators: [
      ["national-minimum-wage-calculator", "Check sleep-in and travel time against NMW."],
      ["holiday-entitlement-calculator", "Calculate holiday for night and bank staff."],
      ["ssp-calculator", "Handle sick pay across a high-absence workforce."],
    ],
    templates: [
      ["right-to-work-check-record", "Evidence sponsor-duty compliance for CQC inspection."],
      ["new-starter-checklist", "Keep safer recruitment onboarding evidenced."],
    ],
  },
  "charities-non-profits": {
    articles: [
      ["uk-redundancy-process-step-by-step", "Restructure when restricted funding ends."],
      ["handling-flexible-working-requests", "Flexible working is a core retention lever on modest pay."],
      ["parental-leave-uk-employer-guide", "Get family leave right with a small back office."],
    ],
    calculators: [
      ["redundancy-pay-calculator", "Budget redundancy costs against grant timelines."],
      ["holiday-entitlement-calculator", "Calculate entitlement for part-time and job-share roles."],
      ["notice-period-calculator", "Confirm notice before a funded post ends."],
    ],
    templates: [
      ["employment-contract-uk", "Separate employed staff from volunteer arrangements."],
      ["new-starter-checklist", "Onboard trustees, staff and secondees consistently."],
    ],
  },
  construction: {
    articles: [
      ["uk-redundancy-process-step-by-step", "Manage headcount between project awards."],
      ["disciplinary-hearing-checklist", "Site conduct and safety breaches need fair process."],
      ["acas-early-conciliation-explained", "Respond correctly to a subcontract worker's claim."],
    ],
    calculators: [
      ["redundancy-pay-calculator", "Cost redundancies at the end of a contract."],
      ["notice-period-calculator", "Check notice for site-based and salaried staff."],
      ["holiday-entitlement-calculator", "Calculate holiday where hours vary by project."],
    ],
  },
  "retail-ecommerce": {
    articles: [
      ["uk-redundancy-process-step-by-step", "Handle store closures and consultation thresholds."],
      ["handling-flexible-working-requests", "Rota changes drive statutory requests."],
      ["disciplinary-hearing-checklist", "Deal with till and stock-loss allegations fairly."],
    ],
    calculators: [
      ["holiday-entitlement-calculator", "Calculate peak-season and part-year holiday."],
      ["national-minimum-wage-calculator", "Check uniform and deduction impacts on NMW."],
      ["redundancy-pay-calculator", "Model costs before a store restructure."],
    ],
  },
  manufacturing: {
    articles: [
      ["uk-redundancy-process-step-by-step", "Run collective consultation on shift closures."],
      ["disciplinary-hearing-checklist", "Address safety and attendance issues on the line."],
      ["acas-early-conciliation-explained", "Know the steps once conciliation starts."],
    ],
    calculators: [
      ["redundancy-pay-calculator", "Cost long-service redundancies accurately."],
      ["notice-period-calculator", "Confirm notice for long-serving operators."],
      ["unfair-dismissal-calculator", "Estimate exposure before you dismiss."],
    ],
  },
  "professional-services": {
    articles: [
      ["workplace-harassment-employer-guide", "Protect fee earners and support staff alike."],
      ["handling-flexible-working-requests", "Hybrid requests are the norm in advisory firms."],
      ["disciplinary-hearing-checklist", "Manage performance and conduct in billable teams."],
    ],
    calculators: [
      ["notice-period-calculator", "Check long notice periods and garden leave."],
      ["unfair-dismissal-calculator", "Estimate exposure for senior exits."],
      ["holiday-entitlement-calculator", "Calculate entitlement for part-time partners and staff."],
    ],
    templates: [
      ["employment-contract-uk", "Set restrictive covenants and notice in writing."],
      ["flexible-working-request-form", "Log hybrid requests and decisions consistently."],
    ],
  },
};

function resolve<T>(
  entries: Array<[string, string]>,
  get: (slug: string) => T | undefined,
  toLink: (item: T, context: string) => PlannedLink
): PlannedLink[] {
  const out: PlannedLink[] = [];
  for (const [slug, context] of entries) {
    const item = get(slug);
    if (item) out.push(toLink(item, context));
  }
  return out;
}

/**
 * Builds the curated internal linking plan for a sector landing page.
 * `serviceSlugs` comes from the industry's recommendedServices so the plan
 * always mirrors what the page already sells.
 */
export function industryLinkPlan(
  industrySlug: string,
  serviceSlugs: string[] = []
): IndustryLinkPlan {
  const spec = { ...DEFAULT_SPEC, ...(SPECS[industrySlug] ?? {}) };

  const services = resolve(
    serviceSlugs.map((s) => [s, ""] as [string, string]),
    getService,
    (s) => ({
      path: `/services/${s.slug}`,
      label: s.name,
      context: s.metaDescription.split(". ")[0] + ".",
    })
  );

  return {
    articles: resolve(spec.articles, getBlogPost, (p, context) => ({
      path: `/hr-insights/${p.slug}`,
      label: p.title,
      context,
    })),
    services,
    calculators: resolve(spec.calculators, getTool, (t, context) => ({
      path: `/hr-calculators/${t.slug}`,
      label: t.name,
      context: context || t.shortDescription,
      slug: t.slug,
      meta: t.category,
    })),
    templates: resolve(spec.templates, getTemplate, (t, context) => ({
      path: `/hr-templates/${t.slug}`,
      label: `${t.title} (${t.format})`,
      context,
      slug: t.slug,
      meta: t.format,
    })),
  };
}

// ============================================================================
// Service page linking plan.
// Each service hub links out to the guidance, calculators, templates and
// sibling services an employer needs right after reading it. Anchor text is
// always descriptive so crawl paths carry topical meaning.
// ============================================================================

interface ServicePlanSpec extends PlanSpec {}

const SERVICE_DEFAULT: ServicePlanSpec = {
  articles: [
    ["disciplinary-hearing-checklist", "Run conduct hearings that stand up to scrutiny."],
    ["uk-redundancy-process-step-by-step", "Follow a defensible redundancy process end to end."],
    ["handling-flexible-working-requests", "Answer statutory requests inside the legal timescales."],
  ],
  services: [
    ["hr-outsourcing", "Hand day-to-day HR admin and advice to a named adviser."],
    ["employment-law-advice", "Get a written answer before you act on a difficult case."],
    ["hr-health-check", "Find the gaps in your contracts, policies and records first."],
  ],
  calculators: [
    ["holiday-entitlement-calculator", "Check part-year and irregular-hours entitlement."],
    ["notice-period-calculator", "Confirm statutory and contractual notice before you write."],
    ["ssp-calculator", "Work out sick pay when absence runs on."],
  ],
  templates: [
    ["employment-contract-uk", "Issue a compliant written statement on day one."],
    ["disciplinary-invitation-letter", "Invite to a hearing with the right wording."],
  ],
};

const SERVICE_SPECS: Record<string, Partial<ServicePlanSpec>> = {
  "hr-consultancy": {
    services: [
      ["hr-outsourcing", "Move from a one-off project to ongoing HR cover."],
      ["hr-health-check", "Audit what you have before you rebuild it."],
      ["staff-handbooks", "Rewrite the policy suite the project exposes."],
    ],
  },
  "health-and-safety-audits": {
    services: [
      ["health-and-safety", "Keep the actions moving with ongoing H&S support."],
      ["risk-assessments", "Rewrite the assessments the audit flags as weak."],
      ["hr-health-check", "Run the same check across your HR paperwork."],
    ],
  },
  "workplace-mediation": {
    services: [
      ["employment-law-advice", "Take advice if mediation is not the right route."],
      ["hr-outsourcing", "Hand the formal process to an HR team if it escalates."],
      ["workplace-mental-health-support", "Support the people affected after the conflict."],
    ],
  },
  "legal-expenses-insurance": {
    services: [
      ["employment-tribunal-support", "See how a claim is actually defended."],
      ["employment-law-advice", "Take the advice your cover depends on."],
      ["hr-outsourcing", "Bundle cover into an ongoing HR retainer."],
    ],
  },
  "risk-assessments": {
    services: [
      ["health-and-safety-audits", "Test whether the assessments work in practice."],
      ["health-and-safety", "Keep assessments reviewed year on year."],
      ["workplace-mental-health-support", "Add a stress risk assessment to the set."],
    ],
  },

  "hr-outsourcing": {
    services: [
      ["hr-health-check", "Start with an audit of what you already have in place."],
      ["employment-law-advice", "Escalate the cases your outsourced team cannot settle."],
      ["staff-handbooks", "Put the policies your adviser will apply in writing."],
    ],
  },
  "employment-law-advice": {
    articles: [
      ["employment-rights-bill-uk-employers", "Track what the Employment Rights Act changes and when."],
      ["acas-early-conciliation-explained", "Know what happens once an employee contacts ACAS."],
      ["disciplinary-hearing-checklist", "Get the process right before advice is needed."],
      ["workplace-harassment-employer-guide", "Meet the preventative duty rather than react to claims."],
    ],
    services: [
      ["employment-tribunal-support", "Defend the claim if conciliation fails."],
      ["hr-outsourcing", "Move from ad-hoc advice to ongoing HR cover."],
      ["staff-handbooks", "Reduce advice calls with clear written policies."],
    ],
    calculators: [
      ["unfair-dismissal-calculator", "Estimate exposure before you dismiss."],
      ["notice-period-calculator", "Check notice entitlement before serving it."],
      ["redundancy-pay-calculator", "Cost statutory payments accurately."],
    ],
  },
  "hr-for-startups": {
    services: [
      ["employment-contract-drafting", "Get your first contracts right before you hire."],
      ["staff-handbooks", "Set expectations while the team is still small."],
      ["recruitment-and-selection", "Hire compliantly from your very first role."],
    ],
    templates: [
      ["employment-contract-uk", "Issue a written statement to your first employees."],
      ["new-starter-checklist", "Onboard consistently as headcount grows."],
    ],
  },
  "hr-for-smes": {
    services: [
      ["hr-outsourcing", "Get HR cover without hiring an in-house team."],
      ["hr-health-check", "Check what needs fixing before you grow further."],
      ["employment-law-advice", "Back your managers with same-day advice."],
    ],
  },
  "tupe-advice": {
    articles: [
      ["uk-redundancy-process-step-by-step", "Handle post-transfer restructures correctly."],
      ["acas-early-conciliation-explained", "Respond properly to a transferring employee's claim."],
      ["disciplinary-hearing-checklist", "Apply fair process to inherited conduct cases."],
    ],
    services: [
      ["redundancy-support", "Plan the restructure that often follows a transfer."],
      ["employment-law-advice", "Check liability and measures before completion."],
      ["employment-contract-drafting", "Harmonise terms after the transfer, lawfully."],
    ],
    calculators: [
      ["redundancy-pay-calculator", "Cost post-transfer redundancies."],
      ["notice-period-calculator", "Check inherited notice terms."],
      ["holiday-entitlement-calculator", "Carry across accrued holiday accurately."],
    ],
    templates: [
      ["redundancy-consultation-letter", "Open consultation with the right wording."],
      ["employment-contract-uk", "Record agreed terms after harmonisation."],
    ],
  },
  "redundancy-support": {
    articles: [
      ["uk-redundancy-process-step-by-step", "Follow the full process, step by step."],
      ["acas-early-conciliation-explained", "Know the next step if someone brings a claim."],
      ["handling-flexible-working-requests", "Consider alternatives to dismissal properly."],
    ],
    services: [
      ["employment-tribunal-support", "Defend unfair dismissal claims that follow."],
      ["employment-law-advice", "Check pooling and selection criteria before you start."],
      ["tupe-advice", "Rule out a transfer before treating it as redundancy."],
    ],
    calculators: [
      ["redundancy-pay-calculator", "Calculate statutory redundancy pay by age and service."],
      ["notice-period-calculator", "Confirm notice and pay in lieu."],
      ["unfair-dismissal-calculator", "Estimate exposure if the process is challenged."],
    ],
    templates: [
      ["redundancy-consultation-letter", "Start consultation on a documented footing."],
      ["employment-contract-uk", "Check the contractual terms you are ending."],
    ],
  },
  "employment-tribunal-support": {
    articles: [
      ["acas-early-conciliation-explained", "Understand the stage before the ET1 lands."],
      ["disciplinary-hearing-checklist", "See what tribunals expect of your process."],
      ["workplace-harassment-employer-guide", "Reduce the claims most likely to reach a hearing."],
    ],
    services: [
      ["employment-law-advice", "Take advice early and avoid the claim."],
      ["hr-health-check", "Fix the paperwork weaknesses claims expose."],
      ["redundancy-support", "Run dismissals that hold up on the day."],
    ],
    calculators: [
      ["unfair-dismissal-calculator", "Estimate the value of the claim against you."],
      ["redundancy-pay-calculator", "Check the statutory sums in dispute."],
      ["notice-period-calculator", "Confirm notice pay claimed alongside."],
    ],
  },
  "hr-health-check": {
    services: [
      ["staff-handbooks", "Close the policy gaps the audit finds."],
      ["employment-contract-drafting", "Replace contracts that fail the written statement rules."],
      ["hr-outsourcing", "Keep the improvements in place month to month."],
    ],
    templates: [
      ["employment-contract-uk", "Benchmark your current contract against a compliant one."],
      ["right-to-work-check-record", "Evidence right to work checks properly."],
    ],
  },
  "health-and-safety": {
    articles: [
      ["disciplinary-hearing-checklist", "Deal fairly with safety breaches."],
      ["workplace-harassment-employer-guide", "Treat harassment as a workplace risk, not just HR."],
      ["uk-redundancy-process-step-by-step", "Restructure without losing competent persons."],
    ],
    services: [
      ["staff-handbooks", "Publish safety policies your team actually reads."],
      ["hr-outsourcing", "Combine HR and safety under one adviser."],
      ["employment-law-advice", "Take advice when safety and conduct overlap."],
    ],
    templates: [
      ["new-starter-checklist", "Evidence induction and safety briefings."],
      ["disciplinary-invitation-letter", "Address serious breaches formally."],
    ],
  },
  "staff-handbooks": {
    articles: [
      ["handling-flexible-working-requests", "Write a policy that matches the statutory process."],
      ["workplace-harassment-employer-guide", "Build the preventative duty into your policies."],
      ["parental-leave-uk-employer-guide", "Get family leave policies right first time."],
    ],
    services: [
      ["employment-contract-drafting", "Keep contracts and handbook consistent."],
      ["hr-health-check", "Find the policies you are missing."],
      ["employment-law-advice", "Check wording before you issue it."],
    ],
    templates: [
      ["flexible-working-request-form", "Log requests and decisions consistently."],
      ["new-starter-checklist", "Issue the handbook as part of onboarding."],
    ],
  },
  "recruitment-and-selection": {
    articles: [
      ["workplace-harassment-employer-guide", "Set the culture from the first interview."],
      ["handling-flexible-working-requests", "Advertise roles with realistic flexibility."],
      ["disciplinary-hearing-checklist", "Deal with probation issues fairly."],
    ],
    services: [
      ["employment-contract-drafting", "Turn an offer into a compliant contract."],
      ["hr-for-startups", "Build a hiring process as you scale."],
      ["hr-outsourcing", "Hand onboarding admin to your adviser."],
    ],
    templates: [
      ["right-to-work-check-record", "Complete and store right to work checks."],
      ["new-starter-checklist", "Onboard every hire the same way."],
    ],
  },
  "employment-contract-drafting": {
    articles: [
      ["handling-flexible-working-requests", "Reflect hybrid and flexible terms accurately."],
      ["parental-leave-uk-employer-guide", "Align contractual family leave with statute."],
      ["uk-redundancy-process-step-by-step", "Understand the terms you rely on when ending employment."],
    ],
    services: [
      ["staff-handbooks", "Pair contracts with the policies they reference."],
      ["employment-law-advice", "Check restrictive covenants before you issue."],
      ["hr-health-check", "Audit the contracts already in circulation."],
    ],
    calculators: [
      ["notice-period-calculator", "Set notice terms against statutory minimums."],
      ["holiday-entitlement-calculator", "Set a holiday year and entitlement that works."],
      ["national-minimum-wage-calculator", "Check contractual pay against NMW rates."],
    ],
    templates: [
      ["employment-contract-uk", "See the structure of a compliant contract."],
      ["new-starter-checklist", "Issue the contract as part of onboarding."],
    ],
  },
  "employee-assistance-programme": {
    articles: [
      ["workplace-harassment-employer-guide", "Handle disclosures that surface through wellbeing support."],
      ["uk-redundancy-process-step-by-step", "Support staff through change and restructure."],
      ["handling-flexible-working-requests", "Use flexibility as a wellbeing lever."],
    ],
    services: [
      ["hr-outsourcing", "Wrap the EAP into day-to-day HR support."],
      ["health-and-safety", "Meet your stress risk assessment duty."],
      ["staff-handbooks", "Write the wellbeing and absence policies around it."],
    ],
    templates: [
      ["wellbeing-policy-uk", "Put the programme into written policy."],
      ["flexible-working-request-form", "Use flexibility to support wellbeing."],
    ],
  },
  "workplace-mental-health-support": {
    articles: [
      ["managing-work-related-stress-risk-assessment", "Run the HSE Management Standards assessment."],
      ["what-is-an-eap-employer-guide", "See how confidential counselling access works."],
      ["workplace-harassment-employer-guide", "Handle disclosures that arrive through wellbeing routes."],
    ],
    services: [
      ["employee-assistance-programme", "Add the confidential counselling helpline."],
      ["hr-outsourcing", "Keep absence, adjustments and advice in one place."],
      ["health-and-safety", "Meet the stress risk assessment duty."],
    ],
    templates: [
      ["wellbeing-policy-uk", "Adopt a written wellbeing and mental health policy."],
      ["flexible-working-request-form", "Use flexibility as a reasonable adjustment."],
    ],
  },
};

/** The editable shape of a link plan (slug + context per group). */
export type LinkPlanSpec = Partial<PlanSpec>;

/** The code-managed default spec for a service page, used as the CMS fallback. */
export function defaultServiceSpec(serviceSlug: string): LinkPlanSpec {
  return { ...SERVICE_DEFAULT, ...(SERVICE_SPECS[serviceSlug] ?? {}) };
}

/** Turns a spec (from code or the CMS) into rendered links. */
export function resolveLinkPlan(spec: LinkPlanSpec, excludeServiceSlug?: string): IndustryLinkPlan {
  const dedupe = (entries: Array<[string, string]>) =>
    entries.filter(([slug]) => slug !== excludeServiceSlug);

  return {
    articles: resolve(spec.articles ?? [], getBlogPost, (p, context) => ({
      path: `/hr-insights/${p.slug}`,
      label: p.title,
      context,
    })),
    services: resolve(dedupe(spec.services ?? []), getService, (s, context) => ({
      path: `/services/${s.slug}`,
      label: s.name,
      context,
    })),
    calculators: resolve(spec.calculators ?? [], getTool, (t, context) => ({
      path: `/hr-calculators/${t.slug}`,
      label: t.name,
      context: context || t.shortDescription,
      slug: t.slug,
      meta: t.category,
    })),
    templates: resolve(spec.templates ?? [], getTemplate, (t, context) => ({
      path: `/hr-templates/${t.slug}`,
      label: `${t.title} (${t.format})`,
      context,
      slug: t.slug,
      meta: t.format,
    })),
  };
}

/** Builds the curated internal linking plan for a service page. */
export function serviceLinkPlan(serviceSlug: string): IndustryLinkPlan {
  return resolveLinkPlan(defaultServiceSpec(serviceSlug), serviceSlug);
}
